import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, Home, Euro, Zap, Shield, Clock } from 'lucide-react';

const BenefitsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedBenefit, setExpandedBenefit] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('benefits');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: Euro,
      title: "80% weniger Heizkosten",
      description: "Moderne Wärmepumpen arbeiten hocheffizient und reduzieren Ihre jährlichen Heizkosten um bis zu 80% gegenüber fossilen Brennstoffen.",
      details: "Eine durchschnittliche Familie spart 1.500-2.500€ pro Jahr an Heizkosten. Bei aktuellen Gas- und Ölpreisen amortisiert sich die Investition bereits nach 5-8 Jahren."
    },
    {
      icon: TrendingUp,
      title: "70% staatliche Förderung",
      description: "Profitieren Sie von der höchsten Förderung aller Zeiten für den Umstieg auf umweltfreundliche Heiztechnik.",
      details: "Bis zu 21.000€ Zuschuss sind möglich. Wir übernehmen die komplette Antragsstellung und sorgen für maximale Förderausschöpfung."
    },
    {
      icon: Home,
      title: "Immobilienwert steigt um 15%",
      description: "Eine moderne Wärmepumpe erhöht nachweislich den Wert Ihrer Immobilie und verbessert die Energieeffizienzklasse.",
      details: "Studien zeigen: Häuser mit Wärmepumpe erzielen 10-15% höhere Verkaufspreise. Zusätzlich verbessert sich oft die Energieeffizienzklasse um 1-2 Stufen."
    },
    {
      icon: Zap,
      title: "Heizen und Kühlen in einem",
      description: "Moderne Wärmepumpen können nicht nur heizen, sondern auch im Sommer für angenehme Kühlung sorgen.",
      details: "Reversible Wärmepumpen bieten ganzjährigen Komfort. Im Winter heizen sie effizient, im Sommer kühlen sie Ihre Räume - alles mit einem System."
    },
    {
      icon: Shield,
      title: "Unabhängig von Gas & Öl",
      description: "Machen Sie sich unabhängig von schwankenden Energiepreisen und unsicheren Lieferketten fossiler Brennstoffe.",
      details: "Mit einer Wärmepumpe sind Sie nicht mehr abhängig von Gas- oder Ölpreisen. Kombiniert mit Photovoltaik erreichen Sie nahezu vollständige Energieautonomie."
    },
    {
      icon: Clock,
      title: "Auch bei Frost hocheffizient",
      description: "Moderne Wärmepumpen arbeiten zuverlässig bis -20°C und erreichen auch bei Minusgraden hohe Effizienzwerte.",
      details: "Inverter-Technologie und optimierte Kältemittel sorgen dafür, dass auch bei -15°C noch Jahresarbeitszahlen von 3-4 erreicht werden."
    }
  ];

  const toggleBenefit = (index) => {
    setExpandedBenefit(expandedBenefit === index ? null : index);
  };

  return (
    <section 
      id="benefits" 
      className="relative py-20 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(239,246,255,0.45) 0%, rgba(255,255,255,1) 40%, rgba(240,253,244,0.5) 100%)'
      }}
    >
      {/* SECTION NUMBERING */}
      <div className="absolute top-4 left-4 z-[60] bg-red-600 text-white px-3 py-2 rounded-lg font-bold text-sm sm:text-base shadow-lg hidden">
        SEKTION 3: VORTEILE
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 relative z-50">
              Ihre Vorteile mit 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-green-dark to-brand-green relative z-50">
                Wärmepumpen
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Entdecken Sie, warum über 2.000 Familien in Berlin & Brandenburg bereits 
              auf unsere Wärmepumpen-Lösungen vertrauen.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              const isExpanded = expandedBenefit === index;
              
              return (
                <div
                  key={index}
                  className={`bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{
                    transitionDelay: `${200 + index * 100}ms`
                  }}
                  onClick={() => toggleBenefit(index)}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-brand-green to-brand-green-hover rounded-xl flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-brand-green">
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 relative z-50">
                      {benefit.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                    
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-gray-700 leading-relaxed text-sm">
                          {benefit.details}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;